import {
  ArrowUpCircle,
  CheckCircle2,
  Clock,
  CreditCard,
  HandshakeIcon,
  Info,
  Link,
  Mail,
  MessageSquare,
  PauseCircle,
  Phone,
  Settings,
  TrendingUp,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { StageBreakdown, StageFunnelData } from '@/types/strategy-diagnostics';

interface StageFunnelTableProps {
  data: StageFunnelData;
}

// ─── Action type helpers ─────────────────────────────────────────────────────

const ACTION_META: Record<
  StageBreakdown['actionType'],
  { label: string; icon: React.ComponentType<{ className?: string }>; color: string }
> = {
  email: {
    label: 'Email',
    icon: Mail,
    color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  },
  phone_call: {
    label: 'Phone Call',
    icon: Phone,
    color: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
  },
  sms: {
    label: 'SMS',
    icon: MessageSquare,
    color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  },
  payment_link: {
    label: 'Payment Link',
    icon: Link,
    color: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
  },
  escalation: {
    label: 'Escalation',
    icon: TrendingUp,
    color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  },
  account_action: {
    label: 'Account Action',
    icon: Settings,
    color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  },
  payment_plan: {
    label: 'Payment Plan',
    icon: CreditCard,
    color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  },
};

// ─── Summary card ─────────────────────────────────────────────────────────────

interface SummaryCardProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number;
  iconClass: string;
  valueClass: string;
  tooltip: string;
}

function SummaryCard({ icon: Icon, label, value, iconClass, valueClass, tooltip }: Readonly<SummaryCardProps>) {
  return (
    <div className="flex flex-col items-center justify-center gap-1 rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900/40 p-3 text-center">
      <Icon className={cn('h-5 w-5', iconClass)} />
      <span className={cn('text-xl font-bold', valueClass)}>{value}</span>
      <div className="flex items-center justify-center gap-1">
        <span className="text-xs text-gray-500 dark:text-gray-400 leading-tight">{label}</span>
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="h-3 w-3 text-gray-400 dark:text-gray-500 cursor-help shrink-0" />
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-56 text-center text-xs leading-snug">
              {tooltip}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    </div>
  );
}

// ─── Derived summary calculations ────────────────────────────────────────────

function deriveSummary(stages: StageBreakdown[]) {
  // Active = sum of activeAtStage across all stages (status: Active)
  const totalActive = stages.reduce((sum, s) => sum + s.activeAtStage, 0);

  // Closed = sum of closedAtStage across all stages (status: Closed)
  const totalClosed = stages.reduce((sum, s) => sum + s.closedAtStage, 0);

  // Promised = sum of promisedAtStage across all stages (status: Promised)
  const totalPromised = stages.reduce((sum, s) => sum + s.promisedAtStage, 0);

  // Escalated = sum of escalatedAtStage across all stages (status: Escalated)
  const totalEscalated = stages.reduce((sum, s) => sum + s.escalatedAtStage, 0);

  // On Hold = sum of onHoldAtStage across all stages (status: On Hold)
  const totalOnHold = stages.reduce((sum, s) => sum + s.onHoldAtStage, 0);

  return { totalActive, totalPromised, totalEscalated, totalOnHold, totalClosed };
}

// ─── Main component ───────────────────────────────────────────────────────────

function getClosedBarColor(percentage: number): string {
  if (percentage >= 40) return 'bg-green-500';
  if (percentage >= 20) return 'bg-amber-500';
  return 'bg-red-500';
}

function getClosedTextColor(percentage: number): string {
  if (percentage >= 40) return 'text-green-700 dark:text-green-400';
  if (percentage >= 20) return 'text-amber-700 dark:text-amber-400';
  return 'text-red-700 dark:text-red-400';
}

export function StageFunnelTable({ data }: Readonly<StageFunnelTableProps>) {
  const { totalCasesEntered, stages } = data;
  const { totalActive, totalPromised, totalEscalated, totalOnHold, totalClosed } = deriveSummary(stages);

  return (
    <div className="space-y-4">
      {/* Summary row — one card per case status */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <SummaryCard
          icon={Clock}
          label="Active"
          value={totalActive}
          iconClass="text-blue-600 dark:text-blue-400"
          valueClass="text-blue-700 dark:text-blue-300"
          tooltip="Cases currently being worked at any stage of the strategy. Maps to case status: Active."
        />
        <SummaryCard
          icon={HandshakeIcon}
          label="Promised"
          value={totalPromised}
          iconClass="text-violet-600 dark:text-violet-400"
          valueClass="text-violet-700 dark:text-violet-300"
          tooltip="Cases where the customer has committed to pay. These exit the collections queue and move into the Promise to Pay (P2P) queue. Maps to case status: Promised."
        />
        <SummaryCard
          icon={ArrowUpCircle}
          label="Escalated"
          value={totalEscalated}
          iconClass="text-orange-600 dark:text-orange-400"
          valueClass="text-orange-700 dark:text-orange-300"
          tooltip="Cases that have been escalated and handed off outside the normal strategy flow. They are no longer driven by this strategy. Maps to case status: Escalated."
        />
        <SummaryCard
          icon={PauseCircle}
          label="On Hold"
          value={totalOnHold}
          iconClass="text-amber-600 dark:text-amber-400"
          valueClass="text-amber-700 dark:text-amber-300"
          tooltip="Cases that are temporarily paused and not progressing through the strategy. Maps to case status: On Hold."
        />
        <SummaryCard
          icon={CheckCircle2}
          label="Closed"
          value={totalClosed}
          iconClass="text-green-600 dark:text-green-400"
          valueClass="text-green-700 dark:text-green-300"
          tooltip="Cases that have been fully resolved and closed. Maps to case status: Closed."
        />
      </div>

      {/* Stage breakdown table */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-800 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50 dark:bg-gray-900/40">
              <TableHead className="w-8 text-center">#</TableHead>
              <TableHead>Stage</TableHead>
              <TableHead>Type</TableHead>
              <TableHead className="text-right">Cases</TableHead>
              <TableHead className="text-right">Closed Here</TableHead>
              <TableHead className="text-right">% Closed Here</TableHead>
              <TableHead className="text-right">Avg Days</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {/* Totals / entry row */}
            <TableRow className="bg-gray-50/50 dark:bg-gray-900/20 font-medium">
              <TableCell className="text-center text-gray-400 dark:text-gray-500 text-xs">—</TableCell>
              <TableCell className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                All Cases Entered
              </TableCell>
              <TableCell />
              <TableCell className="text-right text-sm font-bold text-gray-900 dark:text-gray-100">
                {totalCasesEntered}
              </TableCell>
              <TableCell />
              <TableCell className="text-right text-sm text-gray-400 dark:text-gray-600">—</TableCell>
              <TableCell />
            </TableRow>

            {stages.map((stage) => {
              const meta = ACTION_META[stage.actionType];
              const Icon = meta.icon;

              return (
                <TableRow
                  key={stage.stageOrder}
                  className="hover:bg-gray-50/80 dark:hover:bg-gray-900/30 transition-colors"
                >
                  {/* Stage number */}
                  <TableCell className="text-center">
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-700 text-xs font-semibold text-gray-600 dark:text-gray-300">
                      {stage.stageOrder}
                    </span>
                  </TableCell>

                  {/* Stage label */}
                  <TableCell className="text-sm font-medium text-gray-900 dark:text-gray-100">{stage.label}</TableCell>

                  {/* Action type badge */}
                  <TableCell>
                    <Badge
                      className={cn(
                        'inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border-0',
                        meta.color,
                      )}
                    >
                      <Icon className="h-3 w-3" />
                      {meta.label}
                    </Badge>
                  </TableCell>

                  {/* Cases at stage */}
                  <TableCell className="text-right">
                    <span className="text-sm font-bold text-gray-900 dark:text-gray-100">{stage.casesAtStage}</span>
                  </TableCell>

                  {/* Closed at stage */}
                  <TableCell className="text-right">
                    <span
                      className={cn(
                        'text-sm font-semibold',
                        stage.closedAtStage > 0
                          ? 'text-green-700 dark:text-green-400'
                          : 'text-gray-400 dark:text-gray-600',
                      )}
                    >
                      {stage.closedAtStage > 0 ? `+${stage.closedAtStage}` : '—'}
                    </span>
                  </TableCell>

                  {/* % Closed Here */}
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      {/* Mini fill bar */}
                      <div className="w-16 h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden hidden sm:block">
                        <div
                          className={cn(
                            'h-full rounded-full transition-all',
                            getClosedBarColor(stage.percentageClosedHere),
                          )}
                          style={{ width: `${stage.percentageClosedHere}%` }}
                        />
                      </div>
                      <span
                        className={cn(
                          'text-sm font-semibold tabular-nums',
                          getClosedTextColor(stage.percentageClosedHere),
                        )}
                      >
                        {stage.percentageClosedHere}%
                      </span>
                    </div>
                  </TableCell>

                  {/* Avg days */}
                  <TableCell className="text-right">
                    <span className="text-sm text-gray-600 dark:text-gray-400 tabular-nums">
                      {stage.avgDaysAtStage}d
                    </span>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
